import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider } from './contexts/ThemeContext';

// Global Components
import GlobalDropZone from './components/DragDropUpload/GlobalDropZone';
import CreateProductDialog from './components/CreateProductDialog';

// Pages
import Dashboard from './pages/Dashboard';
import LoginNew from './pages/LoginNew';
import RegisterNew from './pages/RegisterNew';
import ProductDetails from './pages/ProductDetails';
import CommunityViewNew from './pages/CommunityViewNew';
import CommunityView1 from './pages/CommunityView1';
import CommunityView2 from './pages/CommunityView2';
import CommunityView3 from './pages/CommunityView3';
import CommunityView4 from './pages/CommunityView4';
import CreateProductNew from './pages/CreateProductNew';
import CreateCommunityNew from './pages/CreateCommunityNew';
import SearchNew from './pages/SearchNew';
import RecentlyViewedNew from './pages/RecentlyViewedNew';
import WishlistNew from './pages/WishlistNew';
import EditProductNew from './pages/EditProductNew';
import EditCommunityNew from './pages/EditCommunityNew';
import ExploreCommunities from './pages/ExploreCommunities';
import Communities from './pages/Communities';
import Communities2 from './pages/Communities2';
import Communities3 from './pages/Communities3';
import GoMessagesNew from './pages/GoMessagesNew';
import ProdeNew from './pages/ProdeNew';
import StickerAlbum from './pages/StickerAlbum';
import StickerAlbum2 from './pages/StickerAlbum2';
import AlbumDashboard from './pages/AlbumDashboard';

// Mobile alternative — rutas /m/*
import MobileHome from './mobile/MobileHome';
import MobileAlbum from './mobile/MobileAlbum';
import MobileProfile from './mobile/MobileProfile';
import MobileMessages from './mobile/MobileMessages';
import ReservationsNew from './pages/ReservationsNew';
import ProfileNew from './pages/ProfileNew';
import MyProducts from './pages/MyProducts';
import UserProfileNew from './pages/UserProfileNew';
import VerifyEmailNew from './pages/VerifyEmailNew';
import InvitationPageNew from './pages/InvitationPageNew';
import JoinPageNew from './pages/JoinPageNew';
import CommunityAdminNew from './pages/CommunityAdminNew';

// Components
import PrivateRoute from './components/PrivateRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded public landing — keeps the authed bundle untouched.
const Landing = React.lazy(() => import('./pages/Landing'));

function HomeRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Dashboard />;
  }

  return (
    <Suspense
      fallback={
        <div className="flex justify-center items-center min-h-screen">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      }
    >
      <Landing />
    </Suspense>
  );
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <GlobalDropZone>
              <CreateProductDialog />
              <ErrorBoundary>
              <Routes>
              {/* Public routes */}
              <Route path="/landing" element={
                <ErrorBoundary>
                  <Suspense fallback={
                    <div className="flex justify-center items-center min-h-screen">
                      <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin" />
                    </div>
                  }>
                    <Landing />
                  </Suspense>
                </ErrorBoundary>
              } />
              <Route path="/login" element={<ErrorBoundary><LoginNew /></ErrorBoundary>} />
              <Route path="/register" element={<ErrorBoundary><RegisterNew /></ErrorBoundary>} />
              <Route path="/verify-email" element={<ErrorBoundary><VerifyEmailNew /></ErrorBoundary>} />
              <Route path="/invitation" element={<ErrorBoundary><InvitationPageNew /></ErrorBoundary>} />
              <Route path="/join" element={<ErrorBoundary><JoinPageNew /></ErrorBoundary>} />
              {/* Standalone themed pages (own sidebar) */}
              <Route path="/" element={<ErrorBoundary><HomeRoute /></ErrorBoundary>} />
              <Route path="products/:id" element={<PrivateRoute><ErrorBoundary><ProductDetails /></ErrorBoundary></PrivateRoute>} />
              {/* /album es el flow nuevo (slim sidebar + dashboard embebido).
                  /album2 mantiene el flow legacy por si hay que rollbackear. */}
              <Route path="album" element={<PrivateRoute><ErrorBoundary><StickerAlbum2 /></ErrorBoundary></PrivateRoute>} />
              <Route path="album/1" element={<PrivateRoute><ErrorBoundary><StickerAlbum2 albumVariant="1" /></ErrorBoundary></PrivateRoute>} />
              <Route path="album2" element={<PrivateRoute><ErrorBoundary><StickerAlbum /></ErrorBoundary></PrivateRoute>} />
              <Route path="album/dashboard" element={<PrivateRoute><ErrorBoundary><AlbumDashboard /></ErrorBoundary></PrivateRoute>} />
              <Route path="communities" element={<PrivateRoute><ErrorBoundary><Communities /></ErrorBoundary></PrivateRoute>} />
              <Route path="communities2" element={<PrivateRoute><ErrorBoundary><Communities2 /></ErrorBoundary></PrivateRoute>} />
              <Route path="communities3" element={<PrivateRoute><ErrorBoundary><Communities3 /></ErrorBoundary></PrivateRoute>} />
              <Route path="reservations" element={<PrivateRoute><ErrorBoundary><ReservationsNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="profile" element={<PrivateRoute><ErrorBoundary><ProfileNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="my-products" element={<PrivateRoute><ErrorBoundary><MyProducts /></ErrorBoundary></PrivateRoute>} />
              <Route path="create-product" element={<PrivateRoute><ErrorBoundary><CreateProductNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="products/create" element={<PrivateRoute><ErrorBoundary><CreateProductNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="wishlist" element={<PrivateRoute><ErrorBoundary><WishlistNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="recently-viewed" element={<PrivateRoute><ErrorBoundary><RecentlyViewedNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="users/:id" element={<PrivateRoute><ErrorBoundary><UserProfileNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="communities/create" element={<PrivateRoute><ErrorBoundary><CreateCommunityNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="communities/:id" element={<PrivateRoute><ErrorBoundary><CommunityViewNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="communities/:id/edit" element={<PrivateRoute><ErrorBoundary><EditCommunityNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="communities/:id/admin" element={<PrivateRoute><ErrorBoundary><CommunityAdminNew /></ErrorBoundary></PrivateRoute>} />
              {/* A/B/C variants para comparar dónde queda mejor la WeeklyScheduleCard. */}
              <Route path="communities1/:id" element={<PrivateRoute><ErrorBoundary><CommunityView1 /></ErrorBoundary></PrivateRoute>} />
              <Route path="communities2/:id" element={<PrivateRoute><ErrorBoundary><CommunityView2 /></ErrorBoundary></PrivateRoute>} />
              <Route path="communities3/:id" element={<PrivateRoute><ErrorBoundary><CommunityView3 /></ErrorBoundary></PrivateRoute>} />
              <Route path="communities4/:id" element={<PrivateRoute><ErrorBoundary><CommunityView4 /></ErrorBoundary></PrivateRoute>} />
              <Route path="products/:id/edit" element={<PrivateRoute><ErrorBoundary><EditProductNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="search" element={<PrivateRoute><ErrorBoundary><SearchNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="messages" element={<PrivateRoute><ErrorBoundary><GoMessagesNew /></ErrorBoundary></PrivateRoute>} />
              {/* Alias legacy /go-messages → /messages para que bookmarks /
                  push notifs / CTAs viejos no rompan. */}
              <Route path="go-messages" element={<PrivateRoute><ErrorBoundary><GoMessagesNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="prode" element={<PrivateRoute><ErrorBoundary><ProdeNew /></ErrorBoundary></PrivateRoute>} />

              {/* ── Mobile alternative (/m/*) ─────────────────────────── */}
              <Route path="m"          element={<ErrorBoundary><MobileHome /></ErrorBoundary>} />
              <Route path="m/album"    element={<PrivateRoute><ErrorBoundary><MobileAlbum /></ErrorBoundary></PrivateRoute>} />
              <Route path="m/profile"  element={<PrivateRoute><ErrorBoundary><MobileProfile /></ErrorBoundary></PrivateRoute>} />
              <Route path="m/messages" element={<PrivateRoute><ErrorBoundary><MobileMessages /></ErrorBoundary></PrivateRoute>} />
              {/* Aliases que reusan páginas existentes hasta tener variantes mobile dedicadas */}
              <Route path="m/communities"  element={<PrivateRoute><ErrorBoundary><Communities /></ErrorBoundary></PrivateRoute>} />
              <Route path="m/reservations" element={<PrivateRoute><ErrorBoundary><ReservationsNew /></ErrorBoundary></PrivateRoute>} />
              <Route path="m/matches"      element={<PrivateRoute><ErrorBoundary><StickerAlbum /></ErrorBoundary></PrivateRoute>} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </ErrorBoundary>
            </GlobalDropZone>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
